import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Logo } from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useLang } from "../lang/LanguageContext";

const NAV = [
    { label: "Meet Asia", path: "/about" },
    { label: "Our Program", path: "/program" },
    { label: "Safety", path: "/safety" },
    { label: "Enroll", path: "/enroll" },
    { label: "Parents", path: "/parents" },
];

const MOBILE_NAV = [
    { label: "Home", path: "/" },
    { label: "Meet Asia", path: "/about" },
    { label: "A Day at Little Seeds", path: "/program" },
    { label: "Health, Safety & Licensing", path: "/safety" },
    { label: "Enrollment & Tuition", path: "/enroll" },
    { label: "Enrollment Forms", path: "/forms" },
    { label: "Parent Corner & FAQ", path: "/parents" },
    { label: "Schedule a Tour", path: "/contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { t } = useLang();

    useEffect(() => {
        setOpen(false);
    }, [location]);

    return (
        <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-soil/10">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
                <Link to="/" data-testid="header-logo-link" aria-label="Little Seeds Flourish — home">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-link-${item.path.replace("/", "")}`}
                            className={({ isActive }) =>
                                `px-3.5 py-2 rounded-full font-bold text-[15px] transition-colors ${
                                    isActive ? "text-sageink bg-sage/15" : "text-soil hover:text-sageink hover:bg-sage/10"
                                }`
                            }
                        >
                            {t(item.label)}
                        </NavLink>
                    ))}
                    <Link
                        to="/#en-espanol"
                        data-testid="nav-link-en-espanol"
                        className="px-3.5 py-2 rounded-full font-extrabold text-[15px] text-coraldeep hover:bg-coral/10 transition-colors"
                    >
                        En Español
                    </Link>
                </nav>

                <div className="flex items-center gap-2.5">
                    <LanguageToggle className="inline-flex items-center justify-center gap-1 px-2.5 py-2 rounded-full border-2 border-soil/10 bg-white/80 font-extrabold text-xs sm:text-sm text-soil hover:bg-sage/10 transition-colors" />
                    <Link
                        to="/contact"
                        data-testid="header-schedule-tour-button"
                        className="hidden sm:inline-flex items-center gap-2 bg-sun hover:bg-suncdeep text-soil font-extrabold px-5 py-2.5 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all border-2 border-soil/10"
                    >
                        {t("Schedule a Tour")}
                    </Link>
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-button"
                        aria-expanded={open}
                        aria-label={open ? t("Close menu") : t("Open menu")}
                        className="lg:hidden p-2.5 rounded-full bg-white/80 border-2 border-soil/10 text-soil"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="lg:hidden absolute top-[72px] inset-x-0 bg-cream border-b-2 border-soil/10 shadow-xl max-h-[calc(100vh-72px)] overflow-y-auto"
                        data-testid="mobile-menu-panel"
                    >
                        <nav className="px-5 py-4 flex flex-col" aria-label="Mobile navigation">
                            {MOBILE_NAV.map((item, i) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`mobile-nav-link-${item.path.replace("/", "") || "home"}`}
                                    className={({ isActive }) =>
                                        `py-3.5 px-4 font-display font-medium text-xl rounded-2xl ${
                                            isActive ? "bg-sage/15 text-sageink" : "text-soil"
                                        } ${i !== MOBILE_NAV.length - 1 ? "border-b border-soil/5" : ""}`
                                    }
                                >
                                    {t(item.label)}
                                </NavLink>
                            ))}
                            <Link
                                to="/#en-espanol"
                                data-testid="mobile-nav-link-en-espanol"
                                className="py-3.5 px-4 font-display font-medium text-xl rounded-2xl text-coraldeep bg-coral/10 mt-2"
                            >
                                En Español
                            </Link>
                            <div className="flex flex-col gap-2.5 py-5 px-1">
                                <a
                                    href="tel:+15163243418"
                                    data-testid="mobile-call-button"
                                    className="flex items-center gap-3 bg-white border-2 border-soil/10 rounded-2xl px-4 py-3 font-bold text-soil"
                                >
                                    <Phone size={18} className="text-sageink" /> {t("Call or text 516-324-3418")}
                                </a>
                                <a
                                    href="mailto:littleseedsflourish@gmail.com"
                                    data-testid="mobile-email-button"
                                    className="flex items-center gap-3 bg-white border-2 border-soil/10 rounded-2xl px-4 py-3 font-bold text-soil"
                                >
                                    <Mail size={18} className="text-sageink" /> littleseedsflourish@gmail.com
                                </a>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
